import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} StickyConfiguratorModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLElement} content
 */

/**
 * Options modal for the sticky-add-to-cart bar's mobile summary button.
 * Extends DialogComponent (native <dialog>, focus trap, Escape-to-close,
 * scroll-lock — all inherited for free). Its own job is portaling the real
 * <configurator-variant-picker> element in and out of its content container
 * on open/close, using the same Node.append() DOM-move technique already
 * used by configurator-variant-picker.js's own handleOpenInfo() for its
 * info-view/info-dialog split — moving the REAL element (not a clone) means
 * there is no state to synchronize; the configurator's own JS keeps working
 * identically regardless of where in the DOM it currently lives, since its
 * data attributes are read from its own dataset, not derived from ancestry.
 *
 * @extends {DialogComponent<StickyConfiguratorModalRefs>}
 */
class StickyConfiguratorModal extends DialogComponent {
  /** @type {Comment | null} */
  #placeholder = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    // Listened on document (not `this`) because before the first open the
    // configurator-variant-picker element isn't a descendant of this dialog
    // yet — it still lives inside blocks/_product-configurator.liquid. The
    // event is dispatched with bubbles: true and observed the same way by
    // assets/sticky-add-to-cart.js (a non-ancestor sibling), confirming it
    // propagates all the way to document regardless of where the picker
    // currently sits in the tree.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    this.addEventListener('dialog:close', this.#returnConfigurator, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Finds this section's configurator-variant-picker element, moves it into
   * the modal's content container, and opens the dialog. A placeholder
   * comment node is left at the configurator's original position so
   * #returnConfigurator can move it back to the exact same spot.
   */
  openWithConfigurator = () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const configuratorElement = document.getElementById(`ProductConfigurator-${sectionId}`);
    const picker = configuratorElement?.querySelector('configurator-variant-picker');
    if (!picker) return;

    this.#placeholder = document.createComment('sticky-configurator-modal-placeholder');
    picker.before(this.#placeholder);
    this.refs.content.append(picker);

    this.showDialog();
  };

  /**
   * Moves the configurator element back to its placeholder's position and
   * removes the placeholder. Bound to the base DialogComponent's own
   * dialog:close event, so this runs regardless of how the dialog closed
   * (close button, backdrop click, Escape, or #handleCartUpdate below).
   */
  #returnConfigurator = () => {
    if (!this.#placeholder) return;

    const picker = this.refs.content.querySelector('configurator-variant-picker');
    if (picker) {
      this.#placeholder.replaceWith(picker);
    } else {
      this.#placeholder.remove();
    }
    this.#placeholder = null;
  };

  /**
   * Auto-closes the modal on a successful add-to-cart, mirroring
   * assets/quick-add.js's QuickAddDialog.handleCartUpdate — but narrower:
   * that pattern's document-level listener has no product-scoping, which is
   * safe there because quick-add's dialog content is fetched fresh per
   * product on each open. This modal instead portals a persistent,
   * section-scoped configurator element, so a cart update from a DIFFERENT
   * product/section elsewhere on the page (e.g. the cart drawer's own
   * quantity change, or another product's add-to-cart) must NOT close this
   * dialog. Filtered on both event.context (available synchronously, before
   * the promise resolves) and the resolved detail's sourceId (the actual
   * product id that submitted), matched against the currently-portaled
   * picker's own data-product-id.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (!this.refs.dialog.open) return;
    if (event.context !== 'product') return;

    const picker = this.refs.content.querySelector('configurator-variant-picker');
    const portaledProductId = picker?.getAttribute('data-product-id');

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        if (!portaledProductId || detail?.sourceId !== portaledProductId) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-configurator-modal] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('sticky-configurator-dialog')) {
  customElements.define('sticky-configurator-dialog', StickyConfiguratorModal);
}
